/**
 * IncomingCallConnectingOverlay
 *
 * When the user taps "Answer" on an incoming-call push notification,
 * the Service Worker navigates to a URL with `?pstn_answer=<sid>` plus
 * the caller's name/number from the push payload. This component
 * renders the SAME UI the user sees when the app was already open and
 * the SDK delivered an incoming call — caller name, phone, Answer /
 * Decline buttons — INSTANTLY, without waiting for the Twilio Voice
 * SDK to deliver the actual Call object.
 *
 * Behaviour:
 *   • Render IMMEDIATELY on `?pstn_answer=<sid>` with the caller info
 *     parsed from the URL params. Strip the params right away so a
 *     refresh / back-nav doesn't re-trigger.
 *   • Fire-and-forget POST /twilio/calls/wake-incoming/<sid> so the
 *     backend redials NOW (not after a 22s sleep-retry tick).
 *   • If the user taps Answer here, set "armed" — when the SDK's
 *     incoming event arrives, auto-accept silently. The UI stays put
 *     showing a brief "Connecting…" hint.
 *   • If the user taps Decline, fire the existing decline endpoint
 *     and dismiss.
 *   • If the SDK delivers the call BEFORE the user taps anything, our
 *     <IncomingPSTNCallModal/> takes over (its `incoming` becomes
 *     truthy → it renders the official modal). We dismiss this UI to
 *     avoid double modals.
 *   • Auto-clear after 35s if nothing arrives (graceful missed-call).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useTwilioVoice } from '../context/TwilioVoiceContext';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Phone, PhoneOff, PhoneIncoming, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const formatPhone = (raw) => {
  if (!raw) return 'Unknown caller';
  const digits = String(raw).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
};

const IncomingCallConnectingOverlay = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { api, isAuthenticated } = useAuth();
  const { incoming, acceptIncoming, rejectIncoming } = useTwilioVoice() || {};

  const handlingSidRef = useRef(null);
  const [callSid, setCallSid] = useState(null);
  const [callerLabel, setCallerLabel] = useState(null);
  const [callerPhone, setCallerPhone] = useState(null);
  const [armed, setArmed] = useState(false); // user tapped Answer; auto-accept when SDK arrives
  const [seconds, setSeconds] = useState(0);
  const timeoutRef = useRef(null);
  const tickRef = useRef(null);

  const dismiss = () => {
    handlingSidRef.current = null;
    setCallSid(null);
    setCallerLabel(null);
    setCallerPhone(null);
    setArmed(false);
    setSeconds(0);
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    if (tickRef.current)    { clearInterval(tickRef.current);   tickRef.current    = null; }
  };

  // Pick up `?pstn_answer=<sid>&caller_label=...&caller_phone=...`
  // INSTANTLY when the SW navigates here.
  useEffect(() => {
    if (!isAuthenticated) return;
    const sid = searchParams.get('pstn_answer');
    if (!sid) return;
    if (handlingSidRef.current === sid) return;

    handlingSidRef.current = sid;
    setCallSid(sid);
    setCallerLabel(searchParams.get('caller_label'));
    setCallerPhone(searchParams.get('caller_phone'));
    setSeconds(0);

    // Strip the params immediately so refresh / back-nav doesn't re-trigger.
    const next = new URLSearchParams(searchParams);
    next.delete('pstn_answer');
    next.delete('caller_label');
    next.delete('caller_phone');
    next.delete('parent_call_sid');
    const qs = next.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });

    // Wake the backend dial loop NOW so the SDK's incoming event
    // fires in 1-3s instead of 22s.
    api.post(`/twilio/calls/wake-incoming/${encodeURIComponent(sid)}`)
      .catch(() => { /* sleep-retry will fire eventually */ });

    timeoutRef.current = setTimeout(() => {
      toast.message('Call no longer available');
      dismiss();
    }, 35000);
    tickRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
  }, [searchParams, isAuthenticated, api, navigate, location.pathname]);

  // The SDK has now delivered the call. Hand off to <IncomingPSTNCallModal/>.
  // If the user already pressed Answer here, auto-accept silently so they
  // don't have to tap again. Either way, dismiss this overlay so we don't
  // render two modals.
  useEffect(() => {
    if (!callSid) return;
    if (!incoming?.call) return;
    if (armed) {
      try { acceptIncoming?.(); } catch (e) { /* IncomingPSTNCallModal will fall back */ }
    }
    dismiss();
  }, [incoming, callSid, armed, acceptIncoming]);

  if (!callSid) return null;

  const display = callerLabel || formatPhone(callerPhone);

  const onAnswer = () => {
    setArmed(true);
    // If by some race the SDK already has the call, accept now.
    if (incoming?.call) {
      try { acceptIncoming?.(); } catch (e) { /* ignore */ }
      dismiss();
    }
  };

  const onDecline = () => {
    try { rejectIncoming?.(); } catch (e) { /* ignore */ }
    api.post(`/twilio/calls/${encodeURIComponent(callSid)}/decline-incoming`, {})
      .catch(() => { /* call may already be gone */ });
    toast.message('Call declined');
    dismiss();
  };

  return (
    <Dialog
      open
      onOpenChange={() => { /* explicit buttons only — no outside-click dismiss */ }}
    >
      <DialogContent
        className="sm:max-w-sm rounded-3xl bg-[#101010] text-white border-0 [&>button.absolute]:hidden"
        data-testid="incoming-call-connecting-overlay"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Incoming call</DialogTitle>
        <DialogDescription className="sr-only">
          Incoming phone call from {display}
        </DialogDescription>

        <div className="flex flex-col items-center text-center pt-4 pb-2">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-[#586D52]/20 animate-ping" />
            <div className="relative w-20 h-20 rounded-full bg-[#586D52]/20 flex items-center justify-center mb-4">
              <PhoneIncoming className="w-9 h-9 text-[#9bd09b]" />
            </div>
          </div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#9bd09b] mb-1 font-semibold animate-pulse">
            ◉ Ringing — incoming call
          </p>
          <p
            className="text-2xl font-semibold mb-0.5 break-words"
            data-testid="incoming-from"
          >
            {display}
          </p>
          {callerLabel && callerPhone && (
            <p className="text-xs text-white/50">{formatPhone(callerPhone)}</p>
          )}
          <p className="text-xs text-white/40 mt-1">
            {`${String(Math.floor(seconds/60)).padStart(2,'0')}:${String(seconds%60).padStart(2,'0')}`}
          </p>
          {armed ? (
            <div className="mt-3 flex items-center gap-2 text-[#9bd09b] text-xs">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span>Connecting…</span>
            </div>
          ) : (
            <p className="text-[10px] text-white/40 mt-2 italic">
              Tap Answer to pick up, or Decline to send to voicemail.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <Button
            onClick={onDecline}
            disabled={armed}
            className="bg-[#3b1212] hover:bg-[#5a1717] disabled:opacity-50 text-white rounded-2xl h-14 flex flex-col items-center justify-center"
            data-testid="connecting-overlay-reject"
          >
            <PhoneOff className="w-6 h-6" />
            <span className="text-xs mt-1">Decline</span>
          </Button>
          <Button
            onClick={onAnswer}
            disabled={armed}
            className="bg-[#1a4d1a] hover:bg-[#226022] disabled:opacity-70 text-white rounded-2xl h-14 flex flex-col items-center justify-center"
            data-testid="connecting-overlay-accept"
          >
            <Phone className="w-6 h-6" />
            <span className="text-xs mt-1">{armed ? 'Connecting…' : 'Answer'}</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IncomingCallConnectingOverlay;
